export const IMAGE_TO_VIDEO_UPLOAD_LIMITS = {
  maxMb: 50,
};

export const IMAGE_TO_VIDEO_TASK_TYPE = "image-to-video";
export const TEXT_TO_VIDEO_TASK_TYPE = "text-to-video";
export const VIDEO_GENERATION_HISTORY_TASK_TYPES = `${TEXT_TO_VIDEO_TASK_TYPE},${IMAGE_TO_VIDEO_TASK_TYPE}`;
export const DEFAULT_IMAGE_TO_VIDEO_MODEL_ID = "seedance-2-0-fast";

export const IMAGE_TO_VIDEO_MODEL_ICON_BASE =
  "https://static.lip-sync.ai/lipsync/ai-image-to-video/model/";

const AUTO_RATIOS = ["auto"];
const SEEDANCE_RATIOS = ["1:1", "9:16", "16:9", "3:4", "4:3", "21:9", "auto"];
const WIDE_RATIOS = ["16:9", "9:16"];
const STANDARD_TEXT_RATIOS = ["16:9", "9:16", "4:3", "3:4", "1:1"];
const GROK_RATIOS = ["1:1", "2:3", "3:2", "16:9", "9:16", "auto"];
const HAPPYHORSE_TEXT_RATIOS = [
  "9:21",
  "21:9",
  "16:9",
  "9:16",
  "4:3",
  "3:4",
  "4:5",
  "5:4",
  "1:1",
];

export const IMAGE_TO_VIDEO_RATIOS = [
  "auto",
  "16:9",
  "9:16",
  "3:4",
  "4:3",
  "1:1",
  "21:9",
  "2:3",
  "3:2",
  "9:21",
  "4:5",
  "5:4",
];

export const IMAGE_TO_VIDEO_RESOLUTIONS = ["480p", "540p", "720p", "1080p", "4k"];
export const IMAGE_TO_VIDEO_DURATIONS = range(4, 15);

function iconPath(fileName) {
  return `${IMAGE_TO_VIDEO_MODEL_ICON_BASE}${fileName}`;
}

function range(min, max) {
  return Array.from({ length: max - min + 1 }, (_, index) => min + index);
}

function perSecondCredits(creditsPerSecond) {
  return {
    type: "per_second",
    creditsPerSecond,
  };
}

function perTaskResolutionCredits(credits) {
  return {
    type: "per_task_resolution",
    credits,
  };
}

function perTaskDurationResolutionCredits(credits) {
  return {
    type: "per_task_duration_resolution",
    credits,
  };
}

function modeConfig({
  generationType,
  supportedDurations,
  supportedResolutions,
  supportedRatios,
  billing,
  defaults = {},
  sendAspectRatio = true,
  supportsEndImage = false,
  supportsAudio = false,
}) {
  return {
    generationType,
    supportedDurations,
    supportedResolutions,
    supportedRatios,
    billing,
    defaults,
    sendAspectRatio,
    supportsEndImage,
    supportsAudio,
  };
}

function withDefaultMode(model) {
  const imageMode =
    model.modes?.[IMAGE_TO_VIDEO_TASK_TYPE] ||
    Object.values(model.modes || {})[0] ||
    {};

  return {
    ...model,
    generationType: imageMode.generationType,
    supportedDurations: imageMode.supportedDurations,
    supportedRatios: imageMode.supportedRatios,
    supportedResolutions: imageMode.supportedResolutions,
    billing: imageMode.billing,
  };
}

const MODELS = [
  {
    id: "veo-3-1-lite",
    name: "Veo 3.1 Lite",
    provider: "Google",
    providerSlug: "google",
    quality: "lite",
    badge: "Efficient",
    description: "Google motion model tuned for fast, polished social clips.",
    icon: iconPath("google_icon.svg"),
    modes: {
      [IMAGE_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "google_veo3_1_lite_i2v",
        supportedDurations: [4, 6, 8],
        supportedResolutions: ["720p", "1080p", "4k"],
        supportedRatios: WIDE_RATIOS,
        billing: perTaskResolutionCredits({
          "720p": 4,
          "1080p": 5,
          "4k": 16,
        }),
        defaults: {
          duration: 8,
          resolution: "720p",
          aspectRatio: "16:9",
        },
      }),
      [TEXT_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "google_veo3_1_lite_t2v",
        supportedDurations: [4, 6, 8],
        supportedResolutions: ["720p", "1080p", "4k"],
        supportedRatios: WIDE_RATIOS,
        billing: perTaskResolutionCredits({
          "720p": 4,
          "1080p": 5,
          "4k": 16,
        }),
        defaults: {
          duration: 8,
          resolution: "720p",
          aspectRatio: "16:9",
        },
      }),
    },
  },
  {
    id: "veo-3-1-fast",
    name: "Veo 3.1 Fast",
    provider: "Google",
    providerSlug: "google",
    quality: "fast",
    badge: "Fast",
    description: "Higher-fidelity Veo motion with predictable render budgets.",
    icon: iconPath("google_icon.svg"),
    modes: {
      [IMAGE_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "google_veo3_1_fast_i2v",
        supportedDurations: [4, 6, 8],
        supportedResolutions: ["720p", "1080p", "4k"],
        supportedRatios: WIDE_RATIOS,
        billing: perTaskResolutionCredits({
          "720p": 7,
          "1080p": 8,
          "4k": 19,
        }),
        defaults: {
          duration: 8,
          resolution: "720p",
          aspectRatio: "16:9",
        },
      }),
      [TEXT_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "google_veo3_1_fast_t2v",
        supportedDurations: [4, 6, 8],
        supportedResolutions: ["720p", "1080p", "4k"],
        supportedRatios: WIDE_RATIOS,
        billing: perTaskResolutionCredits({
          "720p": 7,
          "1080p": 8,
          "4k": 19,
        }),
        defaults: {
          duration: 8,
          resolution: "720p",
          aspectRatio: "16:9",
        },
      }),
    },
  },
  {
    id: "grok-video-1-5-preview",
    name: "Grok Video 1.5",
    provider: "xAI",
    providerSlug: "xai",
    quality: "creative",
    badge: "Expressive",
    description: "Energetic motion with punchy stylized camera behavior.",
    icon: iconPath("grok-logo.webp"),
    modes: {
      [IMAGE_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "grok_video_1_5_i2v",
        supportedDurations: range(1, 15),
        supportedResolutions: ["480p", "720p"],
        supportedRatios: GROK_RATIOS,
        billing: perSecondCredits({
          "480p": 1,
          "720p": 2,
        }),
        defaults: {
          duration: 6,
          resolution: "720p",
          aspectRatio: "auto",
        },
      }),
    },
  },
  {
    id: "happyhorse-1-1",
    name: "HappyHorse 1.1",
    provider: "HappyHorse",
    providerSlug: "happyhorse",
    quality: "motion",
    badge: "Motion",
    description: "Character-friendly motion with consistent framing.",
    icon: iconPath("happyhorse.png"),
    modes: {
      [IMAGE_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "happyhorse_1_1_i2v",
        supportedDurations: range(3, 15),
        supportedResolutions: ["720p", "1080p"],
        supportedRatios: AUTO_RATIOS,
        billing: perSecondCredits({
          "720p": 5,
          "1080p": 6,
        }),
        defaults: {
          duration: 5,
          resolution: "720p",
          aspectRatio: "auto",
        },
        sendAspectRatio: false,
      }),
      [TEXT_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "happyhorse_1_1_t2v",
        supportedDurations: range(3, 15),
        supportedResolutions: ["720p", "1080p"],
        supportedRatios: HAPPYHORSE_TEXT_RATIOS,
        billing: perSecondCredits({
          "720p": 5,
          "1080p": 6,
        }),
        defaults: {
          duration: 5,
          resolution: "720p",
          aspectRatio: "16:9",
        },
      }),
    },
  },
  {
    id: "seedance-2-0-fast",
    name: "Seedance 2.0 Fast",
    provider: "Seedance",
    providerSlug: "bytedance",
    quality: "fast",
    badge: "Fast",
    description: "Fast Seedance renders for economical iteration.",
    icon: iconPath("Bytedance-logo.webp"),
    modes: {
      [IMAGE_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "seedance_2_0_fast_i2v",
        supportedDurations: range(4, 15),
        supportedResolutions: ["480p", "720p"],
        supportedRatios: SEEDANCE_RATIOS,
        billing: perSecondCredits({
          "480p": 3,
          "720p": 6,
        }),
        defaults: {
          duration: 5,
          resolution: "720p",
          aspectRatio: "auto",
        },
        supportsAudio: true,
      }),
      [TEXT_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "seedance_2_0_fast_t2v",
        supportedDurations: range(4, 15),
        supportedResolutions: ["480p", "720p"],
        supportedRatios: SEEDANCE_RATIOS,
        billing: perSecondCredits({
          "480p": 3,
          "720p": 6,
        }),
        defaults: {
          duration: 5,
          resolution: "720p",
          aspectRatio: "16:9",
        },
        supportsAudio: true,
      }),
    },
  },
  {
    id: "seedance-2-0-mini",
    name: "Seedance 2.0 Mini",
    provider: "Seedance",
    providerSlug: "bytedance",
    quality: "mini",
    badge: "Budget",
    description: "Lean Seedance option for low-credit exploration.",
    icon: iconPath("Bytedance-logo.webp"),
    modes: {
      [IMAGE_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "seedance_2_mini_i2v",
        supportedDurations: range(4, 15),
        supportedResolutions: ["480p", "720p"],
        supportedRatios: SEEDANCE_RATIOS,
        billing: perSecondCredits({
          "480p": 2,
          "720p": 4,
        }),
        defaults: {
          duration: 5,
          resolution: "720p",
          aspectRatio: "auto",
        },
        supportsAudio: true,
      }),
      [TEXT_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "seedance_2_mini_t2v",
        supportedDurations: range(4, 15),
        supportedResolutions: ["480p", "720p"],
        supportedRatios: SEEDANCE_RATIOS,
        billing: perSecondCredits({
          "480p": 2,
          "720p": 4,
        }),
        defaults: {
          duration: 5,
          resolution: "720p",
          aspectRatio: "16:9",
        },
        supportsAudio: true,
      }),
    },
  },
  {
    id: "gemini-omni",
    name: "Gemini Omni",
    provider: "Google",
    providerSlug: "google",
    quality: "omni",
    badge: "Omni",
    description: "Balanced Google video output across common durations.",
    icon: iconPath("google_icon.svg"),
    modes: {
      [IMAGE_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "gemini_omni_i2v",
        supportedDurations: [4, 6, 8, 10],
        supportedResolutions: ["720p", "1080p", "4k"],
        supportedRatios: WIDE_RATIOS,
        billing: perTaskDurationResolutionCredits({
          4: {
            "720p": 10,
            "1080p": 10,
            "4k": 22,
          },
          6: {
            "720p": 13,
            "1080p": 13,
            "4k": 26,
          },
          8: {
            "720p": 16,
            "1080p": 16,
            "4k": 29,
          },
          10: {
            "720p": 19,
            "1080p": 19,
            "4k": 32,
          },
        }),
        defaults: {
          duration: 4,
          resolution: "720p",
          aspectRatio: "16:9",
        },
      }),
      [TEXT_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "gemini_omni_t2v",
        supportedDurations: [4, 6, 8, 10],
        supportedResolutions: ["720p", "1080p", "4k"],
        supportedRatios: WIDE_RATIOS,
        billing: perTaskDurationResolutionCredits({
          4: {
            "720p": 10,
            "1080p": 10,
            "4k": 22,
          },
          6: {
            "720p": 13,
            "1080p": 13,
            "4k": 26,
          },
          8: {
            "720p": 16,
            "1080p": 16,
            "4k": 29,
          },
          10: {
            "720p": 19,
            "1080p": 19,
            "4k": 32,
          },
        }),
        defaults: {
          duration: 4,
          resolution: "720p",
          aspectRatio: "16:9",
        },
      }),
    },
  },
  {
    id: "vidu-q3-pro",
    name: "Vidu Q3 Pro",
    provider: "Vidu",
    providerSlug: "vidu",
    quality: "pro",
    badge: "Pro",
    description: "Reliable image animation with extended duration support.",
    icon: iconPath("vidu_icon.svg"),
    modes: {
      [IMAGE_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "vidu_q3_pro_i2v",
        supportedDurations: range(1, 16),
        supportedResolutions: ["540p", "720p", "1080p"],
        supportedRatios: AUTO_RATIOS,
        billing: perSecondCredits({
          "540p": 2,
          "720p": 3,
          "1080p": 4,
        }),
        defaults: {
          duration: 5,
          resolution: "720p",
          aspectRatio: "auto",
        },
        sendAspectRatio: false,
        supportsAudio: true,
      }),
      [TEXT_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "vidu_q3_pro_t2v",
        supportedDurations: range(1, 16),
        supportedResolutions: ["540p", "720p", "1080p"],
        supportedRatios: STANDARD_TEXT_RATIOS,
        billing: perSecondCredits({
          "540p": 2,
          "720p": 3,
          "1080p": 4,
        }),
        defaults: {
          duration: 5,
          resolution: "720p",
          aspectRatio: "16:9",
        },
        supportsAudio: true,
      }),
    },
  },
  {
    id: "vidu-q3-turbo",
    name: "Vidu Q3 Turbo",
    provider: "Vidu",
    providerSlug: "vidu",
    quality: "turbo",
    badge: "Turbo",
    description: "Low-cost rapid drafts for concept validation.",
    icon: iconPath("vidu_icon.svg"),
    modes: {
      [IMAGE_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "vidu_q3_turbo_i2v",
        supportedDurations: range(1, 16),
        supportedResolutions: ["540p", "720p", "1080p"],
        supportedRatios: AUTO_RATIOS,
        billing: perSecondCredits({
          "540p": 1,
          "720p": 2,
          "1080p": 3,
        }),
        defaults: {
          duration: 5,
          resolution: "720p",
          aspectRatio: "auto",
        },
        sendAspectRatio: false,
        supportsAudio: true,
      }),
      [TEXT_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "vidu_q3_turbo_t2v",
        supportedDurations: range(1, 16),
        supportedResolutions: ["540p", "720p", "1080p"],
        supportedRatios: STANDARD_TEXT_RATIOS,
        billing: perSecondCredits({
          "540p": 1,
          "720p": 2,
          "1080p": 3,
        }),
        defaults: {
          duration: 5,
          resolution: "720p",
          aspectRatio: "16:9",
        },
        supportsAudio: true,
      }),
    },
  },
  {
    id: "veo-3-1",
    name: "Veo 3.1 Quality",
    provider: "Google",
    providerSlug: "google",
    quality: "quality",
    badge: "Quality",
    description: "Highest-credit Veo profile for polished hero clips.",
    icon: iconPath("google_icon.svg"),
    modes: {
      [IMAGE_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "google_veo3_1_quality_i2v",
        supportedDurations: [4, 6, 8],
        supportedResolutions: ["720p", "1080p", "4k"],
        supportedRatios: WIDE_RATIOS,
        billing: perTaskResolutionCredits({
          "720p": 48,
          "1080p": 49,
          "4k": 60,
        }),
        defaults: {
          duration: 8,
          resolution: "720p",
          aspectRatio: "16:9",
        },
      }),
      [TEXT_TO_VIDEO_TASK_TYPE]: modeConfig({
        generationType: "google_veo3_1_quality_t2v",
        supportedDurations: [4, 6, 8],
        supportedResolutions: ["720p", "1080p", "4k"],
        supportedRatios: WIDE_RATIOS,
        billing: perTaskResolutionCredits({
          "720p": 48,
          "1080p": 49,
          "4k": 60,
        }),
        defaults: {
          duration: 8,
          resolution: "720p",
          aspectRatio: "16:9",
        },
      }),
    },
  },
].map(withDefaultMode);

const MODEL_DISPLAY_ORDER = [
  "seedance-2-0-fast",
  "seedance-2-0-mini",
  "gemini-omni",
  "vidu-q3-pro",
  "vidu-q3-turbo",
  "veo-3-1-lite",
  "veo-3-1-fast",
  "veo-3-1",
  "grok-video-1-5-preview",
  "happyhorse-1-1",
];

export const IMAGE_TO_VIDEO_MODELS = [...MODELS].sort((a, b) => {
  const indexA = MODEL_DISPLAY_ORDER.indexOf(a.id);
  const indexB = MODEL_DISPLAY_ORDER.indexOf(b.id);
  return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
});

export function imageToVideoModelSupportsTaskType(model, taskType = IMAGE_TO_VIDEO_TASK_TYPE) {
  return Boolean(model?.modes?.[taskType]);
}

export function imageToVideoModelsForTaskType(taskType = IMAGE_TO_VIDEO_TASK_TYPE) {
  return IMAGE_TO_VIDEO_MODELS.filter((model) =>
    imageToVideoModelSupportsTaskType(model, taskType),
  );
}

export function imageToVideoDefaultModelForTaskType(taskType = IMAGE_TO_VIDEO_TASK_TYPE) {
  return (
    imageToVideoModelsForTaskType(taskType)[0] ||
    IMAGE_TO_VIDEO_MODELS.find((model) => model.id === DEFAULT_IMAGE_TO_VIDEO_MODEL_ID) ||
    IMAGE_TO_VIDEO_MODELS[0]
  );
}

const MODEL_ID_ALIASES = {
  seedance_2_0_fast_i2v: "seedance-2-0-fast",
  seedance_2_0_fast_t2v: "seedance-2-0-fast",
  seedance_2_mini_i2v: "seedance-2-0-mini",
  seedance_2_mini_t2v: "seedance-2-0-mini",
  "seedance-2-mini": "seedance-2-0-mini",
  google_veo3_1_lite_i2v: "veo-3-1-lite",
  google_veo3_1_lite_t2v: "veo-3-1-lite",
  google_veo3_1_fast_i2v: "veo-3-1-fast",
  google_veo3_1_fast_t2v: "veo-3-1-fast",
  google_veo3_1_quality_i2v: "veo-3-1",
  google_veo3_1_quality_t2v: "veo-3-1",
  google_veo_3_1_lite_i2v: "veo-3-1-lite",
  google_veo_3_1_fast_i2v: "veo-3-1-fast",
  google_veo_3_1_quality_i2v: "veo-3-1",
  "veo-3-1-quality": "veo-3-1",
  grok_video_1_5_i2v: "grok-video-1-5-preview",
  "grok-video-1-5": "grok-video-1-5-preview",
  happyhorse_1_1_i2v: "happyhorse-1-1",
  happyhorse_1_1_t2v: "happyhorse-1-1",
  gemini_omni_i2v: "gemini-omni",
  gemini_omni_t2v: "gemini-omni",
  vidu_q3_pro_i2v: "vidu-q3-pro",
  vidu_q3_pro_t2v: "vidu-q3-pro",
  vidu_q3_turbo_i2v: "vidu-q3-turbo",
  vidu_q3_turbo_t2v: "vidu-q3-turbo",
};

function normalizedModelKey(value) {
  return String(value || "").trim();
}

function allGenerationTypes(model) {
  return Object.values(model.modes || {})
    .map((mode) => mode.generationType)
    .filter(Boolean);
}

export function imageToVideoModelById(id) {
  const key = normalizedModelKey(id);
  const alias = MODEL_ID_ALIASES[key];
  const defaultModel =
    IMAGE_TO_VIDEO_MODELS.find((model) => model.id === DEFAULT_IMAGE_TO_VIDEO_MODEL_ID) ||
    IMAGE_TO_VIDEO_MODELS[0];

  return (
    IMAGE_TO_VIDEO_MODELS.find((model) => model.id === key || model.id === alias) ||
    defaultModel
  );
}

export function imageToVideoModelIdFromGenerationType(generationType) {
  const key = normalizedModelKey(generationType);
  const alias = MODEL_ID_ALIASES[key];

  if (alias) return alias;

  const matched = IMAGE_TO_VIDEO_MODELS.find((model) => {
    return (
      model.id === key ||
      model.generationType === key ||
      allGenerationTypes(model).includes(key)
    );
  });

  return matched?.id || DEFAULT_IMAGE_TO_VIDEO_MODEL_ID;
}

export function imageToVideoModeFor(model, taskType = IMAGE_TO_VIDEO_TASK_TYPE) {
  const selectedModel = model || imageToVideoDefaultModelForTaskType(taskType);

  return (
    selectedModel.modes?.[taskType] ||
    imageToVideoDefaultModelForTaskType(taskType)?.modes?.[taskType] ||
    selectedModel.modes?.[IMAGE_TO_VIDEO_TASK_TYPE] ||
    {}
  );
}

export function imageToVideoGenerationTypeFor(
  model,
  taskType = IMAGE_TO_VIDEO_TASK_TYPE,
) {
  return imageToVideoModeFor(model, taskType).generationType || model?.generationType || "";
}

export function imageToVideoSupportedDurationsFor(
  model,
  taskType = IMAGE_TO_VIDEO_TASK_TYPE,
) {
  return imageToVideoModeFor(model, taskType).supportedDurations || IMAGE_TO_VIDEO_DURATIONS;
}

export function imageToVideoSupportedRatiosFor(
  model,
  taskType = IMAGE_TO_VIDEO_TASK_TYPE,
) {
  return imageToVideoModeFor(model, taskType).supportedRatios || IMAGE_TO_VIDEO_RATIOS;
}

export function imageToVideoSupportedResolutionsFor(
  model,
  taskType = IMAGE_TO_VIDEO_TASK_TYPE,
) {
  return imageToVideoModeFor(model, taskType).supportedResolutions || IMAGE_TO_VIDEO_RESOLUTIONS;
}

export function imageToVideoSupportsAspectRatio(
  model,
  taskType = IMAGE_TO_VIDEO_TASK_TYPE,
) {
  const mode = imageToVideoModeFor(model, taskType);

  return mode.sendAspectRatio !== false && imageToVideoSupportedRatiosFor(model, taskType).length > 0;
}

export function imageToVideoSupportsAudio(
  model,
  taskType = IMAGE_TO_VIDEO_TASK_TYPE,
) {
  return Boolean(imageToVideoModeFor(model, taskType).supportsAudio);
}

export function parseImageToVideoAudioParam(value, fallback = true) {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || value === "true") return true;
  if (value === 0 || value === "0" || value === "false") return false;
  if (value == null || value === "") return fallback;
  return Boolean(value);
}

function billingCreditValues(mode) {
  if (!mode?.billing) return [];

  if (mode.billing.type === "per_second") {
    return imageToVideoSupportedDurationsFor({ modes: { [IMAGE_TO_VIDEO_TASK_TYPE]: mode } }).flatMap(
      (duration) =>
        Object.values(mode.billing.creditsPerSecond || {}).map((credits) => credits * duration),
    );
  }

  if (mode.billing.type === "per_task_resolution") {
    return Object.values(mode.billing.credits || {});
  }

  if (mode.billing.type === "per_task_duration_resolution") {
    return Object.values(mode.billing.credits || {}).flatMap((resolutionCredits) =>
      Object.values(resolutionCredits || {}),
    );
  }

  return [];
}

export function imageToVideoCreditHintFor(
  model,
  taskType = IMAGE_TO_VIDEO_TASK_TYPE,
) {
  const mode = imageToVideoModeFor(model, taskType);
  const values = billingCreditValues(mode).filter((value) => Number.isFinite(value));
  const minCredits = values.length ? Math.min(...values) : 0;

  return `${minCredits}+`;
}

export function imageToVideoCreditsFor(
  model,
  duration,
  resolution,
  taskType = IMAGE_TO_VIDEO_TASK_TYPE,
) {
  const mode = imageToVideoModeFor(model, taskType);
  const billing = mode.billing;
  const durationValue = Number(duration) || Number(mode.supportedDurations?.[0]) || 0;
  const resolutionKey = String(resolution || mode.supportedResolutions?.[0] || "").toLowerCase();

  if (!billing) return 0;

  if (billing.type === "per_second") {
    const creditsPerSecond = billing.creditsPerSecond?.[resolutionKey];

    return Number.isFinite(creditsPerSecond) ? creditsPerSecond * durationValue : 0;
  }

  if (billing.type === "per_task_resolution") {
    return billing.credits?.[resolutionKey] || 0;
  }

  if (billing.type === "per_task_duration_resolution") {
    return billing.credits?.[durationValue]?.[resolutionKey] || 0;
  }

  return 0;
}
